import React, { useContext, useEffect, Suspense } from 'react';
import { ChakraProvider } from 'src/components/ui';
import { useRoutes } from 'react-router-dom';
import UserContext from './contexts/user/UserContext';
import FusionLoading from './components/common/FusionLoading';
import ScrollToTop from './components/common/ScrollToTop';
import theme from './theme';
import getRoutes from './routes';
import { ToastContainer } from 'src/services/toast';
import useMyProfile from './hooks/useMyProfile';
import { Providers } from './contexts/Providers';

class ErrorBoundary extends React.Component<
	{ children: React.ReactNode },
	{ hasError: boolean; error?: Error; isReloading: boolean }
> {
	constructor(props: { children: React.ReactNode }) {
		super(props);
		this.state = { hasError: false, isReloading: false };
	}

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error('🚨 ErrorBoundary caught an error:', error, errorInfo);

		// Check if this is a chunk loading error (dynamic import failure) or CSS preload error
		const isChunkLoadError =
			error.message?.includes('Failed to fetch dynamically imported module') ||
			error.message?.includes('Loading chunk') ||
			error.message?.includes('ChunkLoadError') ||
			error.message?.includes('Unable to preload CSS');

		if (isChunkLoadError) {
			console.warn('🔄 Chunk load error detected - attempting automatic reload...');
			// Automatically reload the page once to fetch the new chunks
			// Use sessionStorage to prevent infinite reload loops
			const reloadKey = 'chunk-load-error-reload';
			const hasReloaded = sessionStorage.getItem(reloadKey);

			if (!hasReloaded) {
				sessionStorage.setItem(reloadKey, 'true');
				setTimeout(() => {
					window.location.reload();
				}, 500);
				this.setState({ isReloading: true });
			} else {
				// Clear the flag so future errors can trigger reload
				sessionStorage.removeItem(reloadKey);
			}
		}
	}

	render() {
		if (this.state.isReloading) {
			return (
				<div style={{ padding: '20px', textAlign: 'center' }}>
					<h2>Updating application...</h2>
					<p>Please wait while we reload the latest version.</p>
				</div>
			);
		}

		if (this.state.hasError) {
			return (
				<div style={{ padding: '20px', textAlign: 'center' }}>
					<h2>Something went wrong.</h2>
					<button onClick={() => this.setState({ hasError: false })}>
						Try again
					</button>
				</div>
			);
		}

		return this.props.children;
	}
}

const AppContent: React.FC = () => {
	const { user } = useContext(UserContext);
	const routes = useRoutes(getRoutes(user));
	const profile = useMyProfile();

	return (
		<ChakraProvider theme={theme}>
			<ScrollToTop />
			{profile.isLoading ? (
				<FusionLoading boxProps={{ mt: 8 }} />
			) : (
				<ErrorBoundary>
					<Suspense fallback={<FusionLoading boxProps={{ mt: 8 }} />}>
						{routes}
					</Suspense>
				</ErrorBoundary>
			)}
		</ChakraProvider>
	);
};

const App: React.FC = () => {
	return (
		<Providers>
			<AppContent />
			<ToastContainer />
		</Providers>
	);
};

export default App;
